import { createContext, ReactElement, useMemo, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getRestaurantGroups, getBrandsByRestaurantGroupID, getBrandByID } from '../../api/brand';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BrandContact {
  id: number;
  name: string;
  title: string;
  email: string;
  phone: string;
}

export interface BrandCuisine {
  cuisine_id: number;
  name: string;
  date_created: string;
}

export interface Brand {
  id: string;
  restaurantGroupID: string;
  name: string;
  description: string | null;
  website: string | null;
  email?: string | null;
  phone?: string | null;
  primaryTagline: string | null;
  secondaryTagline: string | null;
  reservationUrl: string | null;
  orderingUrl: string | null;
  cuisineID: number;
  logoUrl: string | null;
  cuisine?: BrandCuisine | null;
  socials?: any | null;
  contacts: BrandContact[];
  giftCardsUrl?: string | null;
}

interface BrandContextInterface {
  brands: Brand[];
  currentBrand: Brand | null;
  setBrand: (_brandId: string) => void;
  refreshBrands: () => Promise<void>;
  refreshCurrentBrand: () => Promise<void>;
  isLoading: boolean;
  restaurantGroupID: string | null;
}

interface BrandProviderInterface {
  children: ReactElement;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const BrandContext = createContext<BrandContextInterface>({
  brands: [],
  currentBrand: null,
  setBrand: () => {},
  refreshBrands: async () => {},
  refreshCurrentBrand: async () => {},
  isLoading: false,
  restaurantGroupID: null
});

// ─── Provider ────────────────────────────────────────────────────────────────

const BrandProvider = ({ children }: BrandProviderInterface) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [currentBrand, setCurrentBrand] = useState<Brand | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [restaurantGroupID, setRestaurantGroupID] = useState<string | null>(null);
  const loadingBrandRef = useRef<string | null>(null);

  // Fetch restaurant groups → brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setIsLoading(true);

        const groups = await getRestaurantGroups();
        if (!groups || groups.length === 0) {
          setIsLoading(false);
          return;
        }
        const groupID = groups[0].id;
        setRestaurantGroupID(groupID);

        const brandsData = await getBrandsByRestaurantGroupID(groupID);
        const brandsWithDefaults: Brand[] = brandsData.map((brand: any) => ({
          ...brand,
          contacts: brand.contacts || []
        }));

        setBrands(brandsWithDefaults);

        if (brandsWithDefaults.length > 0) {
          const detail = await getBrandByID(brandsWithDefaults[0].id);
          const fullBrand: Brand = { ...brandsWithDefaults[0], ...detail, contacts: detail.contacts || [] };
          setCurrentBrand(fullBrand);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const setBrand = useCallback(
    async (brandId: string) => {
      if (loadingBrandRef.current === brandId || currentBrand?.id === brandId) return;
      const selected = brands.find((brand) => brand.id === brandId);
      if (selected) {
        loadingBrandRef.current = brandId;
        setCurrentBrand(selected);
        try {
          const detail = await getBrandByID(brandId);
          const fullBrand: Brand = { ...selected, ...detail, contacts: detail.contacts || [] };
          setCurrentBrand(fullBrand);
        } catch (error) {
          console.error('Error fetching brand detail:', error);
        } finally {
          loadingBrandRef.current = null;
        }
      }
    },
    [brands, currentBrand?.id]
  );

  const refreshBrands = useCallback(async () => {
    if (!restaurantGroupID) return;
    try {
      const brandsData = await getBrandsByRestaurantGroupID(restaurantGroupID);
      const brandsWithDefaults: Brand[] = brandsData.map((brand: any) => ({
        ...brand,
        contacts: brand.contacts || []
      }));
      setBrands(brandsWithDefaults);
    } catch (error) {
      console.error('Error refreshing brands:', error);
    }
  }, [restaurantGroupID]);

  const refreshCurrentBrand = useCallback(async () => {
    if (!currentBrand?.id) return;
    try {
      const detail = await getBrandByID(currentBrand.id);
      setCurrentBrand((prev) => (prev ? { ...prev, ...detail, contacts: detail.contacts || [] } : prev));
    } catch (error) {
      console.error('Error refreshing current brand:', error);
    }
  }, [currentBrand?.id]);

  const value = useMemo(
    () => ({
      brands,
      currentBrand,
      setBrand,
      refreshBrands,
      refreshCurrentBrand,
      isLoading,
      restaurantGroupID
    }),
    [brands, currentBrand, setBrand, refreshBrands, refreshCurrentBrand, isLoading, restaurantGroupID]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrandContext = () => useContext(BrandContext);
export default BrandProvider;
